import React from 'react';
import { useSelector } from 'react-redux';
import { MicrophoneIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import { RootState } from '../../store/store';

const VoiceIndicator: React.FC = () => {
  const { isListening, isProcessing, currentSession, transcript } = useSelector(
    (state: RootState) => state.voice
  );

  if (!isListening && !isProcessing) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {isListening ? (
              <MicrophoneIcon className="w-5 h-5 text-green-500 animate-pulse" />
            ) : (
              <SpeakerWaveIcon className="w-5 h-5 text-blue-500 animate-pulse" />
            )}
            <span className="text-sm font-medium text-gray-900">
              {isListening ? 'Listening...' : 'Processing...'}
            </span>
          </div>

          {currentSession && (
            <span className="text-xs text-gray-500">
              Table {currentSession.tableNumber}
            </span>
          )}
        </div>

        {/* Voice Waveform Animation */}
        <div className="flex items-center justify-center space-x-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`w-1 bg-gradient-to-t from-green-400 to-green-600 rounded-full ${
                isListening ? 'animate-pulse' : ''
              }`}
              style={{
                height: isListening ? `${Math.random() * 20 + 10}px` : '4px',
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>

        {/* Transcript */}
        {transcript && (
          <div className="bg-gray-50 rounded-md p-2 mb-3">
            <p className="text-sm text-gray-700 italic">"{transcript}"</p>
          </div>
        )}

        {/* Status Messages */}
        <div className="text-center">
          {isListening && (
            <p className="text-xs text-gray-600">
              Speak your order clearly...
            </p>
          )}
          {isProcessing && (
            <p className="text-xs text-gray-600">
              Understanding your request...
            </p>
          )}
        </div>

        {/* Pulse Ring Animation */}
        <div className="absolute -inset-1 rounded-lg opacity-30">
          <div
            className={`w-full h-full rounded-lg ${
              isListening
                ? 'bg-green-400 animate-ping'
                : 'bg-blue-400 animate-pulse'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default VoiceIndicator;